/* global Excel */

import { colLetter } from "./sheetBuilder";

/**
 * Builds a polished single-worksheet "Peer Analysis" dashboard: a subject bank
 * plus a hand-picked peer group, driven entirely by live SHEETYBANKS formulas.
 *
 * Two sections:
 *   - Latest-quarter scorecard: one METRICS spill across a fixed ratio set,
 *     with a peer-median row beneath. Field and quarter header cells are live
 *     inputs (the sheetBuilder pattern) — edit them and the grid repoints.
 *   - Five-year trend: a metric dropdown feeds a faint field-header row, so
 *     one pick re-drives 20 quarters × all banks plus a line chart of the
 *     subject vs. the peer median.
 *
 * The intended workflow is: insert the sheet, let it populate, then Save As —
 * the saved .xlsx is the template that gets emailed to a pilot bank.
 */

export interface PeerAnalysisSpec {
  sheetBaseName: string;
  subjectCert: number;
  /** Static label for titles and the chart; the grid itself uses live INFO. */
  subjectName: string;
  peerCerts: number[];
  /** Subtitle line describing how the peer group was chosen. */
  peerGroupLabel: string;
  /** Trend columns, oldest first, in YYYYQN form, e.g. "2021Q2". */
  trendQuarters: string[];
  /** Dropdown choices for the trend metric; the first is the default. */
  trendMetrics: string[];
}

// Scorecard columns: FDIC field codes with the Excel number format that suits
// them. Codes double as the visible headers — they are the live inputs METRICS
// reads, and FDIC shorthand (ROA, NIMY, …) is the audience's native tongue.
const SCORECARD_COLUMNS: { field: string; numberFormat: string }[] = [
  { field: "ASSET", numberFormat: "#,##0" },
  { field: "DEP", numberFormat: "#,##0" },
  { field: "ROA", numberFormat: "0.00" },
  { field: "ROE", numberFormat: "0.00" },
  { field: "NIMY", numberFormat: "0.00" },
  { field: "EEFFR", numberFormat: "0.0" },
  { field: "EQV", numberFormat: "0.00" },
  { field: "NCLNLSR", numberFormat: "0.00" },
  { field: "LNLSDEPR", numberFormat: "0.0" },
];

const COLORS = {
  headerFill: "#1F3864",
  headerText: "#FFFFFF",
  quartersFill: "#D6E4F0",
  subjectFill: "#FFF2CC",
  medianFill: "#F2F2F2",
  inputBorder: "#BFBFBF",
  faintText: "#A6A6A6",
  noteText: "#808080",
};

export async function insertPeerAnalysisSheet(spec: PeerAnalysisSpec): Promise<string> {
  const certs = [spec.subjectCert, ...spec.peerCerts];
  const peerCount = spec.peerCerts.length;
  const quarterCount = spec.trendQuarters.length;

  // --- Scorecard geometry (columns A=cert, B=name, C=city, then metrics) ---
  const scoreHeaderRow = 6;
  const scoreQuarterRow = 7;
  const scoreFirstRow = 8;
  const scoreLastRow = scoreFirstRow + certs.length - 1;
  const scoreMedianRow = scoreLastRow + 1;
  const metricStartCol = 3; // 0-based → column D
  const metricEndCol = metricStartCol + SCORECARD_COLUMNS.length - 1;
  const mStart = colLetter(metricStartCol);
  const mEnd = colLetter(metricEndCol);

  // --- Trend geometry (columns B=label, C… = quarters) ---
  const trendTitleRow = scoreMedianRow + 2;
  const trendFieldRow = trendTitleRow + 1;
  const trendQuarterRow = trendFieldRow + 1;
  const trendSubjectRow = trendQuarterRow + 1;
  const trendMedianRow = trendSubjectRow + 1;
  const trendPeerFirstRow = trendMedianRow + 1;
  const trendPeerLastRow = trendPeerFirstRow + peerCount - 1;
  const qStart = colLetter(2); // C
  const qEnd = colLetter(2 + quarterCount - 1);
  const metricCell = `$${qStart}$${trendTitleRow}`;

  let sheetName = "";
  await Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();

    const existing = worksheets.items.map((s) => s.name);
    sheetName = spec.sheetBaseName;
    for (let i = 2; existing.indexOf(sheetName) !== -1; i++) {
      sheetName = `${spec.sheetBaseName} ${i}`;
    }
    const sheet = worksheets.add(sheetName);

    // --- Title block ---
    sheet.getRange("A1").values = [[`Peer Analysis — ${spec.subjectName}`]];
    sheet.getRange("A2").values = [
      [`FDIC cert ${spec.subjectCert} vs. ${peerCount} peers — ${spec.peerGroupLabel}`],
    ];
    sheet.getRange("A3").values = [
      [
        "Ratios are percentages; $ figures are thousands. Headers are live — a blank quarter means the latest filing. Refresh from the SheetyBanks task pane.",
      ],
    ];
    const title = sheet.getRange("A1").format;
    title.font.bold = true;
    title.font.size = 16;
    const subtitle = sheet.getRange("A2").format;
    subtitle.font.size = 11;
    subtitle.font.color = COLORS.noteText;
    const note = sheet.getRange("A3").format;
    note.font.size = 9;
    note.font.italic = true;
    note.font.color = COLORS.noteText;

    // --- Scorecard: section title and two-row live header ---
    const scoreTitle = sheet.getRange(`A${scoreHeaderRow - 1}`);
    scoreTitle.values = [["Latest quarter scorecard"]];
    scoreTitle.format.font.bold = true;
    scoreTitle.format.font.size = 12;

    const headerRange = sheet.getRange(`A${scoreHeaderRow}:${mEnd}${scoreHeaderRow}`);
    headerRange.values = [["Cert", "Name", "City", ...SCORECARD_COLUMNS.map((c) => c.field)]];
    headerRange.format.font.bold = true;
    headerRange.format.font.color = COLORS.headerText;
    headerRange.format.fill.color = COLORS.headerFill;

    // Quarter inputs under the metric headers: blank = latest. Bordered so
    // they read as editable cells on a gridline-free sheet.
    const quarterLabel = sheet.getRange(`C${scoreQuarterRow}`);
    quarterLabel.values = [["quarters →"]];
    quarterLabel.format.font.size = 9;
    quarterLabel.format.font.color = COLORS.faintText;
    quarterLabel.format.horizontalAlignment = Excel.HorizontalAlignment.right;
    const quarterInputs = sheet.getRange(`${mStart}${scoreQuarterRow}:${mEnd}${scoreQuarterRow}`);
    quarterInputs.format.borders.getItem("EdgeBottom").color = COLORS.inputBorder;
    quarterInputs.format.borders.getItem("EdgeBottom").style = Excel.BorderLineStyle.continuous;

    // --- Scorecard: certs, INFO columns, one spilling METRICS grid ---
    sheet.getRange(`A${scoreFirstRow}:A${scoreLastRow}`).values = certs.map((c) => [c]);
    for (const col of ["B", "C"]) {
      sheet.getRange(`${col}${scoreFirstRow}:${col}${scoreLastRow}`).formulas = certs.map(
        (_c, i) => [`=SHEETYBANKS.INFO($A${scoreFirstRow + i}, ${col}$${scoreHeaderRow})`]
      );
    }
    sheet.getRange(`${mStart}${scoreFirstRow}`).formulas = [
      [
        `=SHEETYBANKS.METRICS(A${scoreFirstRow}:A${scoreLastRow}, ${mStart}${scoreHeaderRow}:${mEnd}${scoreHeaderRow}, ${mStart}${scoreQuarterRow}:${mEnd}${scoreQuarterRow})`,
      ],
    ];

    // Peer median beneath the grid — the subject's row is excluded so the
    // comparison is against the peers, not itself.
    const medianLabel = sheet.getRange(`B${scoreMedianRow}`);
    medianLabel.values = [[`Peer median (${peerCount} peers)`]];
    SCORECARD_COLUMNS.forEach((_c, i) => {
      const col = colLetter(metricStartCol + i);
      sheet.getRange(`${col}${scoreMedianRow}`).formulas = [
        [`=MEDIAN(${col}${scoreFirstRow + 1}:${col}${scoreLastRow})`],
      ];
    });
    const medianRow = sheet.getRange(`A${scoreMedianRow}:${mEnd}${scoreMedianRow}`).format;
    medianRow.font.italic = true;
    medianRow.fill.color = COLORS.medianFill;

    SCORECARD_COLUMNS.forEach((c, i) => {
      const col = colLetter(metricStartCol + i);
      sheet.getRange(`${col}${scoreFirstRow}:${col}${scoreMedianRow}`).numberFormat = certs
        .concat([0])
        .map(() => [c.numberFormat]);
    });

    // Subject row highlight.
    const subjectRow = sheet.getRange(`A${scoreFirstRow}:${mEnd}${scoreFirstRow}`).format;
    subjectRow.fill.color = COLORS.subjectFill;
    subjectRow.font.bold = true;

    // --- Trend: section title and metric dropdown ---
    const trendTitle = sheet.getRange(`A${trendTitleRow}`);
    trendTitle.values = [["Five-year trend"]];
    trendTitle.format.font.bold = true;
    trendTitle.format.font.size = 12;

    // The label watches the snapshot flag (a defined name toggled by
    // freeze/unfreeze): while values are frozen the dropdown still takes a
    // pick, but it only repoints the trend grid after unfreeze — say so.
    // IFERROR degrades to the plain label in never-frozen workbooks, where
    // the name doesn't exist and the reference alone would be #NAME?.
    const pickerLabel = sheet.getRange(`B${trendTitleRow}`);
    pickerLabel.formulas = [
      ['=IF(IFERROR(SheetyBanks_Frozen,FALSE),"Metric (frozen) →","Metric →")'],
    ];
    pickerLabel.format.horizontalAlignment = Excel.HorizontalAlignment.right;
    pickerLabel.format.font.size = 9;
    pickerLabel.format.font.color = COLORS.faintText;

    const picker = sheet.getRange(`${qStart}${trendTitleRow}`);
    picker.values = [[spec.trendMetrics[0]]];
    picker.dataValidation.rule = {
      list: { inCellDropDown: true, source: spec.trendMetrics.join(",") },
    };
    picker.format.font.bold = true;
    picker.format.fill.color = COLORS.subjectFill;
    for (const edge of ["EdgeTop", "EdgeBottom", "EdgeLeft", "EdgeRight"]) {
      picker.format.borders.getItem(edge).color = COLORS.inputBorder;
      picker.format.borders.getItem(edge).style = Excel.BorderLineStyle.continuous;
    }

    // Field header row: every trend column reads the dropdown, so one pick
    // repoints the whole grid. Kept visible but faint — it shows the mechanism.
    const fieldRow = sheet.getRange(`${qStart}${trendFieldRow}:${qEnd}${trendFieldRow}`);
    fieldRow.formulas = [spec.trendQuarters.map(() => `=${metricCell}`)];
    fieldRow.format.font.size = 8;
    fieldRow.format.font.color = COLORS.faintText;

    const quartersHeader = sheet.getRange(`${qStart}${trendQuarterRow}:${qEnd}${trendQuarterRow}`);
    quartersHeader.values = [spec.trendQuarters];
    quartersHeader.format.font.bold = true;
    quartersHeader.format.fill.color = COLORS.quartersFill;

    // --- Trend rows: subject, peer median, then the peers feeding it. The
    // subject and median sit adjacent under the quarter header so the chart
    // can bind one contiguous range.
    sheet.getRange(`A${trendSubjectRow}`).values = [[spec.subjectCert]];
    sheet.getRange(`B${trendSubjectRow}`).formulas = [
      [`=SHEETYBANKS.INFO($A${trendSubjectRow}, "name")`],
    ];
    sheet.getRange(`${qStart}${trendSubjectRow}`).formulas = [
      [
        `=SHEETYBANKS.METRICS(A${trendSubjectRow}, ${qStart}${trendFieldRow}:${qEnd}${trendFieldRow}, ${qStart}${trendQuarterRow}:${qEnd}${trendQuarterRow})`,
      ],
    ];
    const trendSubject = sheet.getRange(`A${trendSubjectRow}:${qEnd}${trendSubjectRow}`).format;
    trendSubject.fill.color = COLORS.subjectFill;
    trendSubject.font.bold = true;

    sheet.getRange(`B${trendMedianRow}`).values = [["Peer median"]];
    spec.trendQuarters.forEach((_q, i) => {
      const col = colLetter(2 + i);
      sheet.getRange(`${col}${trendMedianRow}`).formulas = [
        [`=MEDIAN(${col}${trendPeerFirstRow}:${col}${trendPeerLastRow})`],
      ];
    });
    const trendMedian = sheet.getRange(`A${trendMedianRow}:${qEnd}${trendMedianRow}`).format;
    trendMedian.font.italic = true;
    trendMedian.fill.color = COLORS.medianFill;

    sheet.getRange(`A${trendPeerFirstRow}:A${trendPeerLastRow}`).values = spec.peerCerts.map(
      (c) => [c]
    );
    sheet.getRange(`B${trendPeerFirstRow}:B${trendPeerLastRow}`).formulas = spec.peerCerts.map(
      (_c, i) => [`=SHEETYBANKS.INFO($A${trendPeerFirstRow + i}, "name")`]
    );
    sheet.getRange(`${qStart}${trendPeerFirstRow}`).formulas = [
      [
        `=SHEETYBANKS.METRICS(A${trendPeerFirstRow}:A${trendPeerLastRow}, ${qStart}${trendFieldRow}:${qEnd}${trendFieldRow}, ${qStart}${trendQuarterRow}:${qEnd}${trendQuarterRow})`,
      ],
    ];

    const trendValues = sheet.getRange(`${qStart}${trendSubjectRow}:${qEnd}${trendPeerLastRow}`);
    const trendFormats: string[][] = [];
    for (let r = trendSubjectRow; r <= trendPeerLastRow; r++) {
      trendFormats.push(spec.trendQuarters.map(() => "0.00"));
    }
    trendValues.numberFormat = trendFormats;

    // --- Chart: subject vs. peer median over the quarter header ---
    const chartData = sheet.getRange(`B${trendQuarterRow}:${qEnd}${trendMedianRow}`);
    const chart = sheet.charts.add(Excel.ChartType.line, chartData, Excel.ChartSeriesBy.rows);
    chart.title.text = `${spec.subjectName} vs. peer median`;
    chart.setPosition(
      `${qStart}${trendPeerLastRow + 2}`,
      `${colLetter(12)}${trendPeerLastRow + 20}`
    );

    // --- Layout polish ---
    sheet.showGridlines = false;
    sheet.getRange("A:A").format.columnWidth = 50;
    sheet.getRange("B:B").format.columnWidth = 210;
    sheet.getRange(`C:${qEnd}`).format.columnWidth = 68;
    sheet.getRange(`A${scoreFirstRow}:A${scoreLastRow}`).format.font.bold = true;

    sheet.activate();
    await context.sync();
  });

  return sheetName;
}

// Builds the last `count` quarters ending at `endQuarter` ("YYYYQN"), oldest
// first — literal header values for the trend section.
export function quartersEndingAt(endQuarter: string, count: number): string[] {
  const match = /^(\d{4})Q([1-4])$/.exec(endQuarter);
  if (!match) throw new Error(`Bad quarter: ${endQuarter}`);
  let year = parseInt(match[1], 10);
  let q = parseInt(match[2], 10);
  const result: string[] = [];
  for (let i = 0; i < count; i++) {
    result.unshift(`${year}Q${q}`);
    q -= 1;
    if (q === 0) {
      q = 4;
      year -= 1;
    }
  }
  return result;
}

/**
 * The pilot demo: Ambler Savings Bank against nine southeastern-Pennsylvania
 * community banks of comparable size (~$400M–$1.2B assets, all within ~40
 * miles). The same group falls out of the screener with
 * state = PA, county in (Montgomery, Bucks, Chester, Philadelphia, Berks),
 * asset between 200,000 and 1,200,000.
 */
export const AMBLER_PEER_ANALYSIS: PeerAnalysisSpec = {
  sheetBaseName: "Peer Analysis",
  subjectCert: 28701,
  subjectName: "Ambler Savings Bank",
  peerCerts: [
    30155, // Hatboro Federal Savings, FA — Hatboro (Montgomery)
    31461, // Harleysville Bank — Harleysville (Montgomery)
    35497, // Quaint Oak Bank — Southampton (Bucks)
    7658, // First National Bank and Trust Company of Newtown (Bucks)
    29995, // Phoenixville Federal Bank and Trust (Chester)
    57967, // First Resource Bank — Exton (Chester)
    28836, // United Savings Bank — Philadelphia
    58371, // Hyperion Bank — Philadelphia
    13561, // Fleetwood Bank — Fleetwood (Berks)
  ],
  peerGroupLabel: "southeastern Pennsylvania community banks, $400M–$1.2B assets",
  trendQuarters: quartersEndingAt("2026Q1", 20),
  trendMetrics: ["ROA", "ROE", "NIMY", "EEFFR", "EQV", "NCLNLSR", "LNLSDEPR"],
};

/**
 * Univest Bank and Trust (Souderton, ~$8.2B assets) against nine Pennsylvania
 * commercial banks of comparable scale. Ambler's local small-bank group is the
 * wrong yardstick at this size, so the net widens to statewide. Screener
 * equivalent: state = PA, asset between 3,000,000 and 13,000,000, excluding
 * Dollar Bank (mutual FSB).
 */
export const UNIVEST_PEER_ANALYSIS: PeerAnalysisSpec = {
  sheetBaseName: "Peer Analysis",
  subjectCert: 7759,
  subjectName: "Univest Bank and Trust Co.",
  peerCerts: [
    7468, // First Commonwealth Bank — Indiana
    11124, // S&T Bank — Indiana
    13876, // CNB Bank — Clearfield
    9889, // Mid Penn Bank — Millersburg
    26647, // Firstrust Savings Bank — Conshohocken (Montgomery)
    713, // Orrstown Bank — Harrisburg
    7514, // Peoples Security Bank and Trust Company — Dunmore
    7506, // ACNB Bank — Gettysburg
    30401, // Penn Community Bank — Doylestown (Bucks)
  ],
  peerGroupLabel: "Pennsylvania commercial banks, $3B–$13B assets",
  trendQuarters: quartersEndingAt("2026Q1", 20),
  trendMetrics: ["ROA", "ROE", "NIMY", "EEFFR", "EQV", "NCLNLSR", "LNLSDEPR"],
};

/** Subject banks selectable on the Help → Getting Started peer analysis card. */
export const PEER_ANALYSIS_DEMOS: PeerAnalysisSpec[] = [
  UNIVEST_PEER_ANALYSIS,
  AMBLER_PEER_ANALYSIS,
];
